import { findingTitle, FindingTitle } from "@/lib/finding-title";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState, ErrorState, PermissionState } from "@/components/ui/empty-state";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { api, ApiError, isNotFound } from "@/lib/api";
import type { Vulnerability } from "@/lib/types";
import { CardSkeleton } from "@/components/skeleton";
import { SeverityBadge } from "@/components/severity-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormDialog } from "@/components/ui/form-dialog";
import { Input } from "@/components/ui/input";
import { MetricCard } from "@/components/ui/metric-card";
import { PageHeader, PageShell } from "@/components/ui/page";

export const Route = createFileRoute("/_authed/vulnerabilities/$vulnerabilityId")({
  component: VulnerabilityDetailPage,
});

function VulnerabilityDetailPage() {
  const { vulnerabilityId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [merging, setMerging] = useState(false);
  const [mergeId, setMergeId] = useState("");

  const q = useQuery({
    queryKey: ["vulnerability", vulnerabilityId],
    queryFn: async () => {
      const r = await api.get<Vulnerability>(`/vulnerabilities/${vulnerabilityId}`);
      return r.data;
    },
  });

  const split = useMutation({
    mutationFn: (finding_ids: string[]) =>
      api.post<Vulnerability>(`/vulnerabilities/${vulnerabilityId}/split`, {
        finding_ids,
      }),
    onSuccess: (r) => {
      toast.success("Split into a new vulnerability");
      qc.invalidateQueries({ queryKey: ["vulnerabilities"] });
      if (r.data?.id) {
        void navigate({
          to: "/vulnerabilities/$vulnerabilityId",
          params: { vulnerabilityId: r.data.id },
        });
      }
    },
    onError: (e) =>
      toast.error(e instanceof Error ? e.message : "Split failed"),
  });

  const merge = useMutation({
    mutationFn: (vulnerability_id: string) =>
      api.post<Vulnerability>(`/vulnerabilities/${vulnerabilityId}/merge`, {
        vulnerability_id,
      }),
    onSuccess: () => {
      toast.success("Merged");
      setMerging(false);
      setMergeId("");
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["vulnerability", vulnerabilityId] });
      qc.invalidateQueries({ queryKey: ["vulnerabilities"] });
    },
    onError: (e) =>
      toast.error(e instanceof Error ? e.message : "Merge failed"),
  });

  if (q.error instanceof ApiError && q.error.status === 403) return <PermissionState description="You do not have access to this vulnerability." />;
  if (isNotFound(q.error)) return <EmptyState title="Vulnerability not found" />;
  if (q.isError) return <ErrorState title="Could not load vulnerability" onRetry={() => void q.refetch()} />;
  if (q.isLoading) {
    return (
      <PageShell>
        <CardSkeleton />
        <CardSkeleton />
      </PageShell>
    );
  }
  if (!q.data) return <EmptyState title="Vulnerability not found" />;
  const v = q.data;
  const evidence = v.evidence ?? [];

  function toggle(id: string) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const tools = v.corroborated_by ?? [];

  return (
    <PageShell>
      <PageHeader
        breadcrumbs={[{ label: "Vulnerabilities", link: { to: "/vulnerabilities" } }, { label: "Vulnerability" }]}
        eyebrow={[v.category, v.fine_category, v.confidence, v.baseline_state].filter(Boolean).join(" · ")}
        title={<span className="whitespace-normal break-words"><FindingTitle finding={v} findingId={v.finding_ids?.[0]} /></span>}
        status={<SeverityBadge severity={v.severity} />}
        description={<>
          {v.title !== findingTitle(v) && !v.title.includes(" ") && <span className="block break-all font-mono text-xs">{v.title}</span>}
          {v.merge_reason ? <span className="block">{v.merge_reason}</span> : null}
          <span className="mt-1 flex flex-wrap gap-3 text-xs">
            {v.scan_id ? <Link to="/scans/$scanId" params={{ scanId: v.scan_id }} className="hover:underline">Open in scan</Link> : null}
            {v.repo_id ? <Link to="/repos/$repoId" params={{ repoId: v.repo_id }} className="hover:underline">Open in repo</Link> : null}
          </span>
        </>}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MetricCard title="Evidence" value={v.evidence_count} subtitle="Linked findings" />
        <MetricCard title="Flagged by" value={tools.length} subtitle={tools.join(", ") || "No tools recorded"} />
        <MetricCard title="Confidence" value={v.confidence ? v.confidence.charAt(0).toUpperCase() + v.confidence.slice(1) : "—"} subtitle="Cross-tool agreement" />
        <MetricCard title="Baseline" value={v.baseline_state ? v.baseline_state.charAt(0).toUpperCase() + v.baseline_state.slice(1) : "—"} subtitle="Compared to the baseline scan" />
      </div>

      <FormDialog
        open={merging}
        onOpenChange={(open) => { setMerging(open); if (!open) setMergeId(""); }}
        title="Merge vulnerabilities"
        description="Move the evidence of another vulnerability into this one."
        submitLabel="Merge"
        submitDisabled={!mergeId.trim()}
        pending={merge.isPending}
        dirty={!!mergeId.trim()}
        onSubmit={() => merge.mutate(mergeId.trim())}
      >
        <label className="block">
          <span className="text-xs text-muted-foreground">Merge from vulnerability id</span>
          <Input className="mt-1 font-mono" autoFocus required value={mergeId} onChange={(e) => setMergeId(e.target.value)} />
        </label>
      </FormDialog>

      <Card>
        <CardHeader>
          <CardTitle>Evidence</CardTitle>
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="outline" disabled={selected.size === 0 || selected.size >= evidence.length || split.isPending} onClick={() => split.mutate([...selected])}>
              Split selected
            </Button>
            <Button type="button" size="sm" variant="outline" disabled={merge.isPending} onClick={() => setMerging(true)}>
              Merge…
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {evidence.length === 0 ? (
            <p className="text-sm text-muted-foreground">No linked evidence.</p>
          ) : (
            <ul className="space-y-3">
              {evidence.map((e) => (
                <li key={e.id} className="flex items-start gap-3 text-sm">
                  <Checkbox
                    className="mt-1"
                    checked={selected.has(e.finding_id)}
                    onCheckedChange={() => toggle(e.finding_id)}
                    aria-label={`Select ${findingTitle({ ...e, fine_category: v.fine_category })}`}
                  />
                  <span className="min-w-0 flex-1">
                    <Link to="/findings/$findingId" params={{ findingId: e.finding_id }} className="hover:underline">
                      <FindingTitle finding={{ ...e, fine_category: v.fine_category }} findingId={e.finding_id} />
                    </Link>
                    <span className="block font-mono text-xs text-muted-foreground">
                      {e.tool_name || "tool"}
                      {e.file_path ? ` · ${e.file_path}` : ""}
                      {e.line_start ? `:${e.line_start}` : ""}
                      {e.rule_id ? ` · ${e.rule_id}` : ""}
                      {e.reason ? ` · ${e.reason}` : ""}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </PageShell>
  );
}
